import { useState, useCallback, useMemo, useEffect } from 'react';
import { UseCustomDatePickerProps } from './props';

/**
 * Custom hook to manage date selection and manipulation.
 * This hook provides functionalities to update and manage date states, including day, month, and year.
 *
 * @param {UseCustomDatePickerProps} [options] - Options to initialize the date picker.
 * @param {string} [options.date] - Initial date in string format.
 * @param {Function} [options.callback] - Callback function to be called when the date is updated.
 * @param {boolean} [options.isLoading] - Flag to indicate if the date picker is in a loading state.
 * @returns {Object} An object containing the current date, days, months, years, and update functions.
 *
 * @example
 * // Usage example:
 * const { date, updateDay, updateMonth, updateYear } = useCustomDatePicker({
 *   date: '2023-10-01',
 *   callback: (updatedDate) => function(updatedDate),
 *   isLoading: false,
 * });
 * updateDay(15); // Updates the day to 15
 * updateMonth(12); // Updates the month to December
 * updateYear(2025); // Updates the year to 2025
 */
export function useCustomDatePicker(options?: UseCustomDatePickerProps) {
  const { date: initialDate, callback, isLoading } = options ?? {};
  const [isDateModified, setIsDateModified] = useState(false);

  // State to manage the current date
  const [date, setDate] = useState<Date>(
    initialDate ? new Date(initialDate) : new Date(),
  );

  // Function to reset the date to the initial date if not loading and date is not modified
  function handleResetInitialDate() {
    if (!isLoading && !isDateModified) {
      setDate(new Date(initialDate));
    }
  }

  // Effect to handle resetting the date when dependencies change
  useEffect(handleResetInitialDate, [isLoading, initialDate, isDateModified]);

  // Extract month, year, and day from the current date
  const month = date.getMonth();
  const year = date.getFullYear();
  const day = date.getUTCDate();

  // Callback to get the maximum number of days in a given month and year
  const getMaxDaysForMonth = useCallback(
    (year: number, month: number) => new Date(year, month + 1, 0).getDate(),
    [],
  );

  // Memoized value for the number of days in the current month
  const daysInMonth = useMemo(
    () => getMaxDaysForMonth(year, month),
    [year, month, getMaxDaysForMonth],
  );

  // Memoized array of months (1 to 12)
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);

  // Memoized array of years (current year to 100 years back)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => currentYear - i);
  }, []);

  // Memoized index of the current year in the years array
  const yearIndex = useMemo(
    () => years.findIndex(y => y === year),
    [years, year],
  );

  // Callback to update the day in the current date
  const updateDay = useCallback(
    (newDay: number) => {
      setDate(prev => {
        const updatedDate = new Date(
          prev.getFullYear(),
          prev.getMonth(),
          newDay,
        );
        callback?.(updatedDate.toISOString());
        return updatedDate;
      });
      setIsDateModified(true);
    },
    [callback],
  );

  // Callback to update the month in the current date
  const updateMonth = useCallback(
    (newMonth: number) => {
      setDate(prev => {
        const maxDays = getMaxDaysForMonth(prev.getFullYear(), newMonth - 1);
        const adjustedDay = Math.min(prev.getDate(), maxDays);
        const updatedDate = new Date(
          prev.getFullYear(),
          newMonth - 1,
          adjustedDay,
        );
        callback?.(updatedDate.toISOString());
        return updatedDate;
      });
      setIsDateModified(true);
    },
    [callback, getMaxDaysForMonth],
  );

  // Callback to update the year in the current date
  const updateYear = useCallback(
    (newYear: number) => {
      setDate(prev => {
        const maxDays = getMaxDaysForMonth(newYear, prev.getMonth());
        const adjustedDay = Math.min(prev.getDate(), maxDays);
        const updatedDate = new Date(newYear, prev.getMonth(), adjustedDay);
        callback?.(updatedDate.toISOString());
        return updatedDate;
      });
      setIsDateModified(true);
    },
    [callback, getMaxDaysForMonth],
  );

  // Memoized array of days in the current month
  const days = useMemo(
    () => Array.from({ length: daysInMonth }).map((_, index) => index + 1),
    [daysInMonth],
  );

  return {
    date,
    days,
    months,
    years,
    updateDay,
    updateMonth,
    updateYear,
    current: {
      day: day - 1,
      month,
      year: yearIndex,
      currentYear: year,
    },
  };
}

export type { UseCustomDatePickerProps };
